import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const API_URL = process.env.REACT_APP_API_URL;

// Fetch cart (authenticated only)
export const fetchCart = createAsyncThunk("cart/fetchCart",
    async (token, { rejectWithValue }) => {
        if (!token) return []; // Guest - skip server fetch

        try {
            const res = await fetch(`${API_URL}/carts/me`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed to fetch cart");
            const json = await res.json();
            const products = json?.data?.products || [];
            // Quantities are stored next to the relation in `items`.
            const items = Array.isArray(json?.data?.items) ? json.data.items : [];
            return products.map(item => {
                const line = items.find((i) =>
                    (i.documentId && i.documentId === item.documentId) || i.id === item.id
                );
                return {
                    id: item.id,
                    documentId: item.documentId,
                    title: item.title,
                    new_price: item.new_price,
                    quantity: line?.quantity || 1,
                    img: item.img?.url,
                    stock: item.stock,
                };
            });
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

// Save cart (authenticated only)
export const saveCart = createAsyncThunk("cart/save",
    async ({ token, products }, { rejectWithValue }) => {
        if (!token) return null; // Guest - skip server save

        try {
            const res = await fetch(`${API_URL}/carts/me`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    data: {
                        products: products.map(p => ({
                            id: p.id,
                            documentId: p.documentId,
                            quantity: p.quantity,
                        })),
                    },
                }),
            });
            if (!res.ok) throw new Error("Failed to save cart");
            return await res.json();
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

// Clear cart
export const clearCartOnServer = createAsyncThunk("cart/clear",
    async (token, { rejectWithValue }) => {
        if (!token) return [];

        try {
            const res = await fetch(`${API_URL}/carts/me`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ data: { products: [] } }),
            });
            if (!res.ok) throw new Error("Failed to clear cart");
            return [];
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

const initialState = {
    products: [],
    // Applied discount code: { code, discountType, value } (validated by the backend).
    discount: null,
    status: "idle",
    error: null,
};

const cartSlice = createSlice({
    name: "cart",
    initialState,
    reducers: {
        addToCart: (state, action) => {
            const { id, documentId, quantity, stock } = action.payload;
            // documentId is stable across draft/published rows and republishes;
            // the numeric id is not, so match on either.
            const item = state.products.find((i) =>
                i.id === id || (documentId && i.documentId === documentId)
            );

            if (item) {
                // The payload carries the freshest stock (the product was just loaded).
                const maxStock = stock ?? item.stock ?? Infinity;
                if (maxStock <= 0) return;
                // A sold-out line coming back into stock starts again from zero.
                const base = item.unavailable ? 0 : item.quantity;
                item.stock = stock ?? item.stock;
                item.unavailable = false;
                item.quantity = Math.min(base + quantity, maxStock);
            } else {
                // Known-empty stock never enters the cart; unknown stock is allowed.
                if (stock != null && stock <= 0) return;
                state.products.push({
                    ...action.payload,
                    quantity: Math.min(quantity, stock ?? quantity),
                });
            }
        },
        // if(item) {
        //     const maxStock = item.stock ?? stock ?? Infinity;

        //     if (item.quantity >= maxStock) {
        //         // optionally set a flag or message in state
        //         return;
        //     }

        //     item.quantity = Math.min(item.quantity + quantity, maxStock);
        // }


        removeItem: (state, action) => {
            state.products = state.products.filter((i) => i.id !== action.payload);
        },
        resetCart: (state) => {
            state.products = [];
            state.discount = null;
        },
        // Applies current stock from POST /products/availability. Sold-out
        // lines stay in the bag flagged `unavailable`; lines above stock are
        // reduced to what is left.
        syncStock: (state, action) => {
            for (const result of action.payload || []) {
                const item = state.products.find((p) =>
                    (result.id != null && p.id === result.id) ||
                    (result.documentId && p.documentId === result.documentId)
                );
                if (!item) continue;
                if (result.documentId) item.documentId = result.documentId;
                item.stock = result.stock ?? undefined;
                item.unavailable = !result.available;
                if (result.available && result.stock != null && item.quantity > result.stock) {
                    item.quantity = result.stock;
                }
            }
        },
        removeUnavailable: (state) => {
            state.products = state.products.filter((p) => !p.unavailable);
        },
        setDiscount: (state, action) => {
            state.discount = action.payload;
        },
        clearDiscount: (state) => {
            state.discount = null;
        },
        updateQuantity: (state, action) => {
            const { id, quantity } = action.payload;
            const item = state.products.find((p) => p.id === id);
            if (item) item.quantity = Math.max(1, Math.min(quantity, item.stock ?? quantity));
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCart.fulfilled, (state, action) => {
                state.status = "succeeded";
                const server = action.payload;
                const isSame = (a, b) =>
                    a.id === b.id || (a.documentId && a.documentId === b.documentId);
                // Keep lines added as a guest before logging in; the save effect
                // then writes the merged cart back to the server.
                const localOnly = state.products.filter((p) => !server.some((s) => isSame(s, p)));
                state.products = [...server, ...localOnly];
            })
            .addCase(clearCartOnServer.fulfilled, (state) => {
                state.products = [];
            });
    },
});

export const {
    addToCart,
    removeItem,
    resetCart,
    updateQuantity,
    syncStock,
    removeUnavailable,
    setDiscount,
    clearDiscount,
} = cartSlice.actions;
export default cartSlice.reducer;
